// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useMask, xFieldValidator, xValidationProps } from "@guestsupply/XDL/Utilities";
import xDefaultRules from "@guestsupply/XDL/Utilities/xValidate/xDefaultRules";
import FormField, { FormFieldComponentProps, FormFieldPresentationProps } from "@insite/mobius/FormField";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import * as React from "react";
import styled, { withTheme } from "styled-components";

export interface TextAreaPresentationProps extends FormFieldPresentationProps<TextAreaComponentProps> {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<TextAreaComponentProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type TextAreaComponentProps = MobiusStyledComponentProps<
    "textarea",
    {
        /** Error message to be displayed below the textarea. */
        error?: React.ReactNode;
        /** Hint text to be displayed below the textarea. */
        hint?: React.ReactNode;
        /** Label to be displayed above the textarea. */
        label?: React.ReactNode;
        // XNG-Changes
        type?: string;
    } & Partial<FormFieldComponentProps> &
        // XNG-Changes
        xValidationProps
>;

export type TextAreaProps = TextAreaComponentProps & TextAreaPresentationProps;

export const TextAreaStyle = styled.textarea<InjectableCss>`
    ${injectCss}
`;

/**
 * TextArea is a form element with an optional label, hint text, error message and optional icon.
 * Props not contained in the list below get passed into the input component (e.g. event handlers, `value`, etc).
 */
const TextArea: React.FC<TextAreaProps & HasDisablerContext> = withTheme(props => {
    // <XNG-Changes>
    const [internalError, setInternalError] = React.useState<string>("");
    const [touched, setTouched] = React.useState<boolean>(false);
    // </XNG-Changes>
    const {
        disable,
        disabled,
        // XNG-Changes: change the name to errorProp
        error: errorProp,
        hint,
        id,
        placeholder,
        required,
        mergeCss,
        // XNG-Changes
        validationRules,
        formId,
        type = "notes",
        ...otherProps
    } = props;
    // <XNG-Changes>
    const error = errorProp || internalError;
    const fieldValidator = xFieldValidator(
        {
            label: otherProps.label as string,
            type,
            validationRules,
            value: otherProps.value || otherProps.defaultValue,
            onError: setInternalError,
            required,
            touched,
        },
        formId,
    );
    const internalOnBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        fieldValidator.onBlur(event);
        otherProps.onBlur?.(event);
        setTouched(true);
    };
    const internalKeyUp = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        fieldValidator.onKeyUp(event);
        otherProps.onKeyUp?.(event);
    };
    const unMaskedValueChange = (event: React.ChangeEvent<HTMLTextAreaElement>, masked: string, original: string) => {
        otherProps.onChange?.({
            ...event,
            target: {
                ...event.target,
                value: original,
            },
        });
    };
    const internalOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const schema = validationRules || xDefaultRules.notes;
        useMask(schema || "", otherProps.value as string, unMaskedValueChange)(event);
    };
    // </XNG-Changes>

    const { applyStyledProp } = applyPropBuilder(otherProps, { component: "textArea", category: "formField" });

    const isDisabled = !!(disable || disabled);
    const inputId = id || uniqueId();
    const labelId = `${inputId}-label`;
    const inputLabelObj = otherProps.label === 0 || otherProps.label ? { "aria-labelledby": labelId } : {};
    const descriptionId = `${inputId}-description`;
    const hasDescription = error || hint;

    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.textArea?.defaultProps?.mergeCss;

    const TextArea = React.useMemo(
        () => (
            <TextAreaStyle
                {...omitMultiple(otherProps, [
                    "sizeVariant",
                    "border",
                    "label",
                    "backgroundColor",
                    "cssOverrides",
                    // XNG-Changes
                    "formId",
                    "validationRules",
                ])}
                {...{ disabled: isDisabled, placeholder, required: formId ? undefined : required }}
                {...inputLabelObj}
                css={applyStyledProp("css", resolvedMergeCss)}
                id={inputId}
                aria-describedby={hasDescription ? descriptionId : undefined}
                aria-invalid={!!error}
                aria-required={!disabled && required}
                tabIndex={0}
                // XNG-Changes
                onBlur={internalOnBlur}
                onKeyUp={internalKeyUp}
                onChange={internalOnChange}
                maxLength={otherProps.maxLength || 500}
            />
        ),
        [
            isDisabled,
            placeholder,
            required,
            otherProps,
            inputId,
            inputLabelObj,
            hasDescription,
            descriptionId,
            error,
            disabled,
            required,
        ],
    );

    return (
        <FormField
            {...props}
            required={required}
            descriptionId={descriptionId}
            disabled={isDisabled}
            formInput={TextArea}
            labelId={labelId}
            inputId={inputId}
            // XNG-Changes
            error={error}
        />
    );
});

TextArea.defaultProps = {
    rows: 4,
};

TextArea.displayName = "TextArea";
/** @component */
export default withDisabler(TextArea);
